import React, { useState } from 'react';
import {
  Card,
  Table,
  Form,
  Select,
  Avatar,
  Descriptions,
  Button,
  Modal,
  Input,
  message,
} from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTable } from '@/hooks/useTable';
import {
  getUserPointsRecordsManagement,
  updateUserPoints,
  getUserPointsInfoNew,
} from '@/api/userPoints';

const pointsTypeOptions = [
  { label: '全部', value: '' },
  { label: '增加积分', value: 1 },
  { label: '扣减积分', value: 0 },
];

const UserPointsPage: React.FC = () => {
  const { userId = '' } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [pointsForm] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [adjustType, setAdjustType] = useState<1 | 0>(1);

  // 获取用户积分信息
  const { data: userPointsInfo, refetch: refetchUserInfo } = useQuery({
    queryKey: ['user-points-info', userId],
    queryFn: async () => {
      const response = await getUserPointsInfoNew(userId);
      return response.data;
    },
  });

  const {
    data,
    total,
    currentPage,
    pageSize,
    isLoading,
    handleTableChange,
    updateParams,
    refresh
  } = useTable({
    queryKey: ['user-points-list', userId].join(','),
    queryFn: async (current, size, params) => {
      const response = await getUserPointsRecordsManagement({
        ...params,
        userId,
        ...form.getFieldsValue(),
        currentPage: current,
        pageSize: size,
      });
      return response.data;
    },
  });

  const handleSearch = async () => {
    refresh()
  };

  const handleReset = () => {
    form.resetFields();
    refresh()
  };

  const showPointsModal = (type: 1 | 0) => {
    setAdjustType(type);
    setIsModalVisible(true);
    pointsForm.resetFields();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    pointsForm.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await pointsForm.validateFields();
      await updateUserPoints({
        userId,
        operatorType: adjustType,
        points: Number(values.points),
      });
      message.success('积分调整成功');
      setIsModalVisible(false);
      pointsForm.resetFields();

      form.resetFields();
      refresh();
      refetchUserInfo();
    } catch (error) {
      console.error('积分调整失败:', error);
      message.error('积分调整失败');
    }
  };

  const columns = [
    {
      title: '积分ID',
      dataIndex: 'id',
      width: 200,
    },
    {
      title: '积分类型',
      dataIndex: 'pointsOpType',
      width: 100,
      render: (type: number) => (type === 1 ? '获得' : '消耗'),
    },
    {
      title: '积分值',
      dataIndex: 'points',
      width: 100,
      render: (points: number, record: any) => (
        <span
          style={{ color: record.pointsOpType === 1 ? '#52c41a' : '#ff4d4f' }}
        >
          {record.pointsOpType === 1 ? '+' : '-'}
          {points}
        </span>
      ),
    },
    {
      title: '积分场景',
      dataIndex: 'pointsSenseTitle',
      width: 150,
    },
    {
      title: '场景描述',
      dataIndex: 'pointsSenseDesc',
      width: 200,
    },
    {
      title: '场景图标',
      dataIndex: 'pointsSenseIconUrl',
      width: 100,
      render: (url: string) => (url ? <Avatar src={url} /> : '-'),
    },
    {
      title: '业务ID',
      dataIndex: 'bizId',
      width: 200,
      render: (bizId: string) => bizId || '-',
    },
    {
      title: '额外数据',
      dataIndex: 'extData',
      width: 200,
      render: (extData: any) => (extData ? JSON.stringify(extData) : '-'),
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: 200,
      render: (source: string) => source || '-',
    },
    {
      title: '操作时间',
      dataIndex: 'createTime',
      width: 160,
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <Card
      title="用户积分明细"
      extra={<Button onClick={() => navigate(-1)}>返回</Button>}
    >
      {userPointsInfo && (
        <Card style={{ marginBottom: 16 }}>
          <div
            style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}
          >
            <Avatar size={64} src={userPointsInfo.headImageUrl} />
            <div style={{ marginLeft: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                {userPointsInfo.name}
              </div>
              <div>用户ID: {userPointsInfo.userNumber}</div>
            </div>
          </div>
          <Descriptions column={3}>
            <Descriptions.Item label="可用积分">
              {userPointsInfo.remainingPoints}
            </Descriptions.Item>
            <Descriptions.Item label="累计获得积分">
              {userPointsInfo.totalPoints}
            </Descriptions.Item>
            <Descriptions.Item label="累计消耗积分">
              {userPointsInfo.usagePoints}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              onClick={() => showPointsModal(1)}
              style={{ marginRight: 16 }}
            >
              增加积分
            </Button>
            <Button danger onClick={() => showPointsModal(0)}>
              扣减积分
            </Button>
          </div>
        </Card>
      )}

      <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item name="pointsOpType" label="积分类型" initialValue={pointsTypeOptions[0].value}>
          <Select style={{ width: 120 }} options={pointsTypeOptions} />
        </Form.Item>
        <Form.Item>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="primary" onClick={handleSearch}>
              搜索
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </div>
        </Form.Item>
      </Form>

      <Modal
        title={adjustType === 1 ? '增加积分' : '扣减积分'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      >
        <Form form={pointsForm} layout="vertical">
          <Form.Item
            name="points"
            label="积分数量"
            rules={[
              { required: true, message: '请输入积分数量' },
              { pattern: /^[1-9]\d*$/, message: '请输入大于0的整数' },
            ]}
          >
            <Input placeholder="请输入积分数量" />
          </Form.Item>
        </Form>
      </Modal>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        scroll={{ x: 1200 }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
        }}
        loading={isLoading}
        onChange={handleTableChange}
      />
    </Card>
  );
};

export default UserPointsPage;
